import React from 'react';
import { HiOutlineRefresh } from 'react-icons/hi';

interface ConfirmationOverlayProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  secondaryDescription?: string;
  confirmText?: string;
  loadingText?: string;
  isLoading?: boolean;
  /** Color theme: 'amber' | 'blue' | 'red'. Defaults to 'amber' */
  variant?: 'amber' | 'blue' | 'red';
  /** Custom icon. Defaults to HiOutlineRefresh */
  icon?: React.ReactNode;
}

const variantStyles = {
  amber: {
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-600 dark:text-amber-400',
    confirmBtn: 'bg-amber-600 hover:bg-amber-700',
  },
  blue: {
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
    confirmBtn: 'bg-blue-600 hover:bg-blue-700',
  },
  red: {
    iconBg: 'bg-red-100 dark:bg-red-900/30',
    iconColor: 'text-red-600 dark:text-red-400',
    confirmBtn: 'bg-red-600 hover:bg-red-700',
  },
};

export const ConfirmationOverlay: React.FC<ConfirmationOverlayProps> = ({
  isOpen,
  onCancel,
  onConfirm,
  title,
  description,
  secondaryDescription,
  confirmText = 'Confirm',
  loadingText = 'Processing...',
  isLoading = false,
  variant = 'amber',
  icon,
}) => {
  if (!isOpen) return null;

  const styles = variantStyles[variant];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={() => !isLoading && onCancel()} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 w-10 h-10 rounded-full ${styles.iconBg} flex items-center justify-center`}>
            {icon || <HiOutlineRefresh className={`w-5 h-5 ${styles.iconColor}`} />}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {description}
            </p>
            {secondaryDescription && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {secondaryDescription}
              </p>
            )}
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium text-white ${styles.confirmBtn} rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50`}
          >
            {icon || <HiOutlineRefresh className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />}
            {isLoading ? loadingText : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationOverlay;
